import {useMemo} from "react"
import {createMuiTheme} from "@material-ui/core/styles"
import {pink, blue, grey, green, lightBlue, deepPurple, blueGrey} from "@material-ui/core/colors"
import useMediaQuery from "@material-ui/core/useMediaQuery"

export default function useTheme(themeColor = null) {
  const prefersDarkMode = useMediaQuery(`(prefers-color-scheme: dark)`)
  const colorMode = themeColor || (prefersDarkMode ? `dark` : `light`)
  const bgColor = colorMode === `dark` ? grey[900] : grey[50]
  const fgColor = colorMode === `dark` ? grey[50] : `black`
  const linkForeColor = colorMode === `dark` ? lightBlue[500] : lightBlue[500]
  const linkHoverColor = colorMode === `dark` ? deepPurple[900] : blue[100]
  const scrollBarForeColor = colorMode === `dark` ? grey[800] : blueGrey[200]
  const scrollBarBackColor = colorMode === `dark` ? `#282828` : blueGrey[100]

  return useMemo(
    () =>
      createMuiTheme({
        props: {
          // 各コンポーネントのpropsのデフォルト値を変更
          MuiContainer: {},
          MuiButton: {
            color: `primary`,
            variant: `contained`,
          },
          MuiBadge: {
            color: `secondary`,
          },
          MuiTextField: {
            variant: `outlined`, // テキストフィールドのデフォルトをアウトラインにする
          },
          MuiCheckbox: {
            color: `primary`,
          },
          MuiRadio: {
            color: `primary`,
          },
          MuiSwitch: {
            color: `primary`,
          },
          MuiList: {
            dense: false,
          },
          MuiTable: {
            size: `small`,
          },
        },
        palette: {
          type: colorMode,
          background: {
            default: bgColor,
            main: green.A700,
            select: blue.A700,
          },
          primary: {
            main: blue.A700,
            contrastText: fgColor,
          },
          secondary: {
            main: pink.A400,
            contrastText: fgColor,
          },
          linkForeColor,
          linkHoverColor,
          scrollBarForeColor,
          scrollBarBackColor,
        },
        typography: {
          // htmlFontSize: 16,
          // fontSize: 16,
          allVariants: {
            color: fgColor,
          },
          h1: {
            fontSize: `3rem`,
          },
          h2: {
            fontSize: `2.5rem`,
          },
          h3: {
            fontSize: `2rem`,
          },
          h4: {
            fontSize: `1.75rem`,
          },
          h5: {
            fontSize: `1.5rem`,
          },
          h6: {
            fontSize: `1.25rem`,
          },
          subtitle1: {
            fontSize: `1rem`,
          },
          subtitle2: {
            fontSize: `1rem`,
          },
          body1: {
            fontSize: `1rem`,
          },
          body2: {
            fontSize: `1rem`,
          },
          button: {
            fontSize: `1rem`,
            textTransform: `none`, // ボタンのラベルを自動で大文字にしない
          },
          caption: {
            fontSize: `1rem`,
          },
          overline: {
            fontSize: `1rem`,
          },
        },
        mixins: {
          toolbar: {
            minHeight: 50,
          },
        },
        overrides: {
          MuiPagination: {
            ul: {
              justifyContent: `center`,
            },
          },
          MuiDialog: {
            root: {
              // zIndex: `1200 !important`,
            },
            paperFullWidth: {
              height: `inherit`,
            },
          },
          // MuiPickersBasePicker: {
          //   pickerView: {
          //     zIndex: 100000000000000,
          //     maxWidth: 500,
          //   },
          // },
          MuiAutocomplete: {
            paper: {
              color: fgColor,
            },
            tag: {
              margin: `10px 5px 5px`,
            },
          },
          MuiAppBar: {
            colorPrimary: {
              backgroundColor: bgColor,
            },
          },
          MuiToolbar: {
            regular: {
              paddingTop: 18,
              paddingBottom: 18,
            },
          },
          MuiBox: {
            root: {
              marginTop: 50,
            },
          },
          MuiPaper: {
            root: {
              backgroundColor: bgColor,
            },
          },
          MuiButton: {
            root: {
              textTransform: `none`,
              color: `white`,
            },
            label: {
              fontSize: `1rem`,
            },
            containedPrimary: {
              color: `white`,
            },
          },
          MuiTab: {
            textColorInherit: {
              fontSize: `1rem`,
            },
          },
          MuiInputLabel: {
            root: {
              "&$focused": {
                fontWeight: `bold`,
              },
            },
            outlined: {
              "&$shrink": {
                // zIndex: 1202,
              },
              transform: `translate(14px, 10px) scale(1)`,
            },
          },
          MuiInput: {},
          MuiOutlinedInput: {
            input: {
              WebkitBoxShadow: `0 0 0 100px ${bgColor} inset`,
            },
            root: {
              "&$error $notchedOutline": {
                borderColor: pink.A700,
              },
            },
          },
          MuiFormLabel: {
            root: {
              "&$focused": {
                fontWeight: `bold`,
              },
              "&$error": {
                color: pink.A700,
              },
            },
          },
          MuiFormHelperText: {
            root: {
              "&$error": {
                color: pink.A700,
              },
            },
          },
          MuiFormControl: {
            root: {
              width: `-webkit-fill-available`,
            },
          },
          MuiContainer: {
            root: {
              marginTop: 80,
            },
          },
          MuiList: {},
          MuiListItem: {},
          MuiListItemText: {
            root: {
              flex: `none`,
              minWidth: 200,
            },
          },
          MuiSwitch: {},
          MuiTypography: {
            colorTextSecondary: {
              color: fgColor,
            },
          },
          MuiPickersToolbarText: {
            toolbarBtnSelected: {
              display: `none`,
            },
            toolbarTxt: {
              fontSize: `-webkit-xxx-large`,
            },
          },
          MuiSlider: {
            valueLabel: {
              fontSize: `1rem`,
              top: -22,
              "& *": {
                background: `transparent`,
                color: fgColor,
              },
            },
            thumb: {
              height: 24,
              width: 24,
              marginTop: -11,
            },
          },
        },
      }),
    [prefersDarkMode, themeColor, colorMode, fgColor, bgColor],
  )
  // return responsiveFontSizes(theme)
}
